import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import AdminLayout from "@/components/AdminLayout";
import { PageHeader, EmptyState, LoadingSkeleton, FormField } from "@/components/ui-system";
import { can } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Map, X, Plus, Save, Compass, Loader2, AlertCircle, RotateCcw, Info } from "lucide-react";

/**
 * B2C Zone Configuration (admin). Contract "Page 3".
 * - GET/PUT  /api/b2c/admin/zone-config   (PUT gated by b2c.zones:manage)
 * - POST     /api/b2c/admin/zone-config/test
 * Editor for same_city_match_digits (1-6) + three prefix lists (chip inputs),
 * plus an always-available zone tester and an A-F legend (B2C_PLAN §1.4).
 */

// A-F legend per docs/B2C_PLAN.md §1.4 (column definitions of the rate card).
const ZONE_LEGEND = [
  { zone: "A", label: "Within city", hint: "Same-city / local — pickup & drop share the configured pincode-prefix match." },
  { zone: "B", label: "Within region", hint: "Same state / region." },
  { zone: "C", label: "Metro to metro", hint: "Between metro cities (metro prefixes)." },
  { zone: "D", label: "Rest of India", hint: "Default zone for anything not otherwise classified." },
  { zone: "E", label: "Special (NE / J&K / islands)", hint: "North-East, Jammu & Kashmir, island territories." },
  { zone: "F", label: "Extended", hint: "Far/extended destinations — served by only some couriers." },
];

// Tailwind tint per zone letter (design-system semantic hues; no raw hex).
const ZONE_BADGE_CLASS = {
  A: "bg-blue-100 text-blue-800",
  B: "bg-cyan-100 text-cyan-800",
  C: "bg-teal-100 text-teal-800",
  D: "bg-slate-100 text-slate-800",
  E: "bg-amber-100 text-amber-800",
  F: "bg-violet-100 text-violet-800",
};

const PREFIX_LISTS = [
  {
    key: "metro_prefixes",
    title: "Metro prefixes",
    description: "Pincode prefixes that identify metro cities (drives Zone C metro-to-metro).",
    testid: "metro",
  },
  {
    key: "zone_e_prefixes",
    title: "Zone E prefixes",
    description: "Special destinations — North-East, J&K, island territories.",
    testid: "zone-e",
  },
  {
    key: "zone_f_prefixes",
    title: "Zone F prefixes",
    description: "Extended / far destinations served by only some couriers.",
    testid: "zone-f",
  },
];

const PREFIX_RE = /^\d{2,6}$/;

// Extract a human string from a FastAPI error body (string detail, or [{row,error}]/422 array).
const extractError = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d?.row != null && d?.error) return `Row ${d.row}: ${d.error}`;
        if (d?.loc && d?.msg) return `${d.loc.join(".")}: ${d.msg}`;
        return d?.error || d?.msg || JSON.stringify(d);
      })
      .join("; ");
  }
  if (detail && typeof detail === "object") return detail.message || JSON.stringify(detail);
  return error?.message || fallback;
};

const arraysEqual = (a, b) => a.length === b.length && a.every((v, i) => v === b[i]);

const B2CZones = ({ user, onLogout }) => {
  const canManage = can(user, "b2c.zones:manage");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);

  const [config, setConfig] = useState(null); // raw server doc (baseline)
  // editable working copy
  const [matchDigits, setMatchDigits] = useState(1);
  const [matchDigitsError, setMatchDigitsError] = useState("");
  const [lists, setLists] = useState({
    metro_prefixes: [],
    zone_e_prefixes: [],
    zone_f_prefixes: [],
  });
  // per-list draft text in the chip-input box
  const [drafts, setDrafts] = useState({
    metro_prefixes: "",
    zone_e_prefixes: "",
    zone_f_prefixes: "",
  });

  // Zone tester state
  const [testPickup, setTestPickup] = useState("");
  const [testDrop, setTestDrop] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { zone, reason }

  const applyServerDoc = useCallback((doc) => {
    setConfig(doc);
    setMatchDigits(Number(doc?.same_city_match_digits ?? 1));
    setLists({
      metro_prefixes: Array.isArray(doc?.metro_prefixes) ? [...doc.metro_prefixes] : [],
      zone_e_prefixes: Array.isArray(doc?.zone_e_prefixes) ? [...doc.zone_e_prefixes] : [],
      zone_f_prefixes: Array.isArray(doc?.zone_f_prefixes) ? [...doc.zone_f_prefixes] : [],
    });
    setDrafts({ metro_prefixes: "", zone_e_prefixes: "", zone_f_prefixes: "" });
    setMatchDigitsError("");
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axios.get(`${API}/b2c/admin/zone-config`);
      applyServerDoc(res.data);
    } catch (error) {
      const msg = extractError(error, "Failed to load zone configuration");
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [applyServerDoc]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // ---- dirty tracking ----
  const dirty = useMemo(() => {
    if (!config) return false;
    if (Number(config.same_city_match_digits ?? 1) !== Number(matchDigits)) return true;
    return PREFIX_LISTS.some((l) => !arraysEqual(lists[l.key], config[l.key] || []));
  }, [config, matchDigits, lists]);

  const matchDigitsValid = useMemo(() => {
    const n = Number(matchDigits);
    return Number.isInteger(n) && n >= 1 && n <= 6;
  }, [matchDigits]);

  // ---- chip list editing ----
  const addPrefix = (key, rawValue) => {
    const value = String(rawValue ?? "").trim();
    if (!value) return;
    // allow comma/space-separated bulk paste
    const tokens = value.split(/[\s,]+/).filter(Boolean);
    const invalid = [];
    setLists((prev) => {
      const next = [...prev[key]];
      tokens.forEach((tok) => {
        if (!PREFIX_RE.test(tok)) {
          invalid.push(tok);
          return;
        }
        if (!next.includes(tok)) next.push(tok);
      });
      return { ...prev, [key]: next };
    });
    if (invalid.length) {
      toast.error(`Ignored invalid prefix${invalid.length > 1 ? "es" : ""}: ${invalid.join(", ")} — use 2-6 digits.`);
    }
    setDrafts((prev) => ({ ...prev, [key]: "" }));
  };

  const removePrefix = (key, value) => {
    setLists((prev) => ({ ...prev, [key]: prev[key].filter((v) => v !== value) }));
  };

  const handleDraftKeyDown = (key, e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addPrefix(key, drafts[key]);
    } else if (e.key === "Backspace" && !drafts[key] && lists[key].length) {
      // backspace on empty box removes last chip
      removePrefix(key, lists[key][lists[key].length - 1]);
    }
  };

  // ---- save (PUT only changed lists + digits) ----
  const handleSave = async () => {
    if (!canManage) return;
    if (!matchDigitsValid) {
      setMatchDigitsError("Must be a whole number 1-6.");
      toast.error("Same-city match digits must be between 1 and 6.");
      return;
    }
    // Validate every chip again defensively.
    for (const l of PREFIX_LISTS) {
      const bad = lists[l.key].filter((p) => !PREFIX_RE.test(p));
      if (bad.length) {
        toast.error(`${l.title}: invalid entr${bad.length > 1 ? "ies" : "y"} ${bad.join(", ")} — each must be 2-6 digits.`);
        return;
      }
    }

    const payload = {};
    if (Number(config.same_city_match_digits ?? 1) !== Number(matchDigits)) {
      payload.same_city_match_digits = Number(matchDigits);
    }
    PREFIX_LISTS.forEach((l) => {
      if (!arraysEqual(lists[l.key], config[l.key] || [])) {
        payload[l.key] = lists[l.key];
      }
    });

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save.");
      return;
    }

    setSaving(true);
    try {
      const res = await axios.put(`${API}/b2c/admin/zone-config`, payload);
      applyServerDoc(res.data);
      toast.success("Zone configuration saved.");
    } catch (error) {
      toast.error(extractError(error, "Failed to save zone configuration"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (config) applyServerDoc(config);
  };

  // ---- zone tester ----
  const handleTest = async (e) => {
    if (e) e.preventDefault();
    const pickup = testPickup.trim();
    const drop = testDrop.trim();
    if (!/^\d{6}$/.test(pickup) || !/^\d{6}$/.test(drop)) {
      toast.error("Enter a valid 6-digit pickup and drop pincode.");
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/b2c/admin/zone-config/test`, {
        pickup_pincode: pickup,
        drop_pincode: drop,
      });
      setTestResult(res.data);
    } catch (error) {
      toast.error(extractError(error, "Zone test failed"));
    } finally {
      setTesting(false);
    }
  };

  // ---------------- render ----------------
  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="b2c-zones-page">
        <PageHeader
          title="B2C Zone Configuration"
          subtitle="Zones A-F drive rate-card billing. A pickup + drop pincode resolves to a zone, which selects the charged rate."
          badge={
            config?.version != null ? (
              <Badge variant="secondary" data-testid="zone-config-version">
                v{config.version}
              </Badge>
            ) : null
          }
        />

        {!canManage && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            data-testid="zones-readonly-banner"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>You have view-only access. The zone tester works, but editing the configuration requires the <span className="font-medium">b2c.zones:manage</span> permission.</span>
          </div>
        )}

        {loading ? (
          <LoadingSkeleton variant="form" rows={6} data-testid="zones-loading" />
        ) : loadError ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="py-12">
              <EmptyState
                icon={AlertCircle}
                title="Couldn't load zone configuration"
                description={loadError}
                action={
                  <Button onClick={fetchConfig} data-testid="zones-retry-btn">
                    <RotateCcw className="h-4 w-4" />
                    Retry
                  </Button>
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* ---- Editor (spans 2 cols on lg) ---- */}
            <div className="space-y-6 lg:col-span-2">
              <Card className="border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-slate-800">Configuration</CardTitle>
                  <CardDescription>
                    Same-city matching and the prefix lists that classify pincodes into zones.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* same_city_match_digits */}
                  <div className="max-w-xs">
                    <FormField
                      label="Same-city match digits"
                      htmlFor="same-city-match-digits"
                      required
                      hint="How many leading pincode digits must match for pickup & drop to count as same-city (Zone A). 1-6."
                      error={matchDigitsError}
                    >
                      <Input
                        id="same-city-match-digits"
                        type="number"
                        min={1}
                        max={6}
                        step={1}
                        value={matchDigits}
                        disabled={!canManage}
                        data-testid="same-city-match-digits-input"
                        onChange={(e) => {
                          const v = e.target.value;
                          setMatchDigits(v === "" ? "" : parseInt(v, 10));
                          setMatchDigitsError("");
                        }}
                        onBlur={() => {
                          if (!matchDigitsValid) setMatchDigitsError("Must be a whole number 1-6.");
                        }}
                      />
                    </FormField>
                  </div>

                  {/* prefix lists */}
                  {PREFIX_LISTS.map((l) => (
                    <div key={l.key} className="space-y-2" data-testid={`prefix-list-${l.testid}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-700">{l.title}</p>
                        <p className="text-xs text-slate-500">{l.description}</p>
                      </div>

                      <div
                        className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-2 focus-within:ring-1 focus-within:ring-ring"
                      >
                        {lists[l.key].length === 0 && !canManage && (
                          <span className="px-1 text-xs text-slate-400">No prefixes configured.</span>
                        )}
                        {lists[l.key].map((prefix) => (
                          <span
                            key={prefix}
                            className="inline-flex items-center gap-1 rounded-md bg-brand-bg px-2 py-0.5 text-xs font-medium text-slate-700"
                            data-testid={`chip-${l.testid}-${prefix}`}
                          >
                            {prefix}
                            {canManage && (
                              <button
                                type="button"
                                aria-label={`Remove ${prefix}`}
                                className="rounded-full text-slate-400 hover:text-red-600 focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                onClick={() => removePrefix(l.key, prefix)}
                                data-testid={`chip-remove-${l.testid}-${prefix}`}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ))}
                        {canManage && (
                          <input
                            type="text"
                            inputMode="numeric"
                            value={drafts[l.key]}
                            placeholder={lists[l.key].length ? "Add…" : "Add a 2-6 digit prefix"}
                            aria-label={`Add prefix to ${l.title}`}
                            className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none placeholder:text-slate-400"
                            data-testid={`prefix-input-${l.testid}`}
                            onChange={(e) => setDrafts((prev) => ({ ...prev, [l.key]: e.target.value }))}
                            onKeyDown={(e) => handleDraftKeyDown(l.key, e)}
                            onBlur={() => drafts[l.key] && addPrefix(l.key, drafts[l.key])}
                          />
                        )}
                        {canManage && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => addPrefix(l.key, drafts[l.key])}
                            disabled={!drafts[l.key]}
                            data-testid={`prefix-add-btn-${l.testid}`}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        )}
                      </div>
                      <p className="text-xs text-slate-400">
                        {lists[l.key].length} prefix{lists[l.key].length === 1 ? "" : "es"} · press Enter or comma to add
                      </p>
                    </div>
                  ))}

                  {/* save row */}
                  {canManage && (
                    <div className="flex flex-col gap-2 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                      <span className="text-xs text-slate-500" data-testid="zones-dirty-indicator">
                        {dirty ? "You have unsaved changes." : "All changes saved."}
                      </span>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleReset}
                          disabled={!dirty || saving}
                          data-testid="zones-reset-btn"
                        >
                          <RotateCcw className="h-4 w-4" />
                          Discard
                        </Button>
                        <Button
                          type="button"
                          onClick={handleSave}
                          disabled={!dirty || saving || !matchDigitsValid}
                          data-testid="zones-save-btn"
                        >
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                          {saving ? "Saving…" : "Save configuration"}
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* ---- Right column: tester + legend ---- */}
            <div className="space-y-6">
              {/* Zone tester */}
              <Card className="border-0 shadow-sm" data-testid="zone-tester-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading text-lg text-slate-800">
                    <Compass className="h-5 w-5 text-brand" />
                    Zone tester
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex"
                            tabIndex={0}
                            aria-label="How the state is determined"
                            data-testid="zone-tester-info"
                          >
                            <Info className="h-4 w-4 text-slate-400" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs leading-relaxed">
                          State is determined offline — no external API. It's read from the pincode's
                          first 2 digits using India Post's postal-circle allocation (e.g. 80 → Bihar,
                          56 → Karnataka). This is a fast built-in mapping and an approximation: a few
                          prefixes span more than one state, so the dominant state is used. Only the
                          metro / Zone E / Zone F prefixes and same-city digits above are editable —
                          the pincode → state map is fixed in code.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                  <CardDescription>Resolve a pickup + drop pincode against the current saved config.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleTest} className="space-y-4">
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <FormField label="Pickup pincode" htmlFor="test-pickup">
                        <Input
                          id="test-pickup"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="110001"
                          value={testPickup}
                          data-testid="zone-test-pickup-input"
                          onChange={(e) => setTestPickup(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        />
                      </FormField>
                      <FormField label="Drop pincode" htmlFor="test-drop">
                        <Input
                          id="test-drop"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="560001"
                          value={testDrop}
                          data-testid="zone-test-drop-input"
                          onChange={(e) => setTestDrop(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        />
                      </FormField>
                    </div>
                    <Button
                      type="submit"
                      className="w-full"
                      disabled={testing || testPickup.length !== 6 || testDrop.length !== 6}
                      data-testid="zone-test-btn"
                    >
                      {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />}
                      {testing ? "Resolving…" : "Resolve zone"}
                    </Button>
                  </form>

                  {testResult && (
                    <div
                      className="mt-4 flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
                      data-testid="zone-test-result"
                    >
                      <div
                        className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-xl text-3xl font-bold font-heading ${
                          ZONE_BADGE_CLASS[testResult.zone] || "bg-slate-100 text-slate-800"
                        }`}
                        data-testid="zone-test-result-zone"
                      >
                        {testResult.zone ?? "?"}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Resolved zone</p>
                        <p className="text-sm font-semibold text-slate-800">
                          {ZONE_LEGEND.find((z) => z.zone === testResult.zone)?.label || "Unknown"}
                        </p>
                        {testResult.reason && (
                          <p className="mt-1 break-words text-xs text-slate-600" data-testid="zone-test-result-reason">
                            {testResult.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* A-F legend */}
              <Card className="border-0 shadow-sm" data-testid="zone-legend-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 font-heading text-lg text-slate-800">
                    <Map className="h-5 w-5 text-brand" />
                    Zone legend
                  </CardTitle>
                  <CardDescription>What each zone means (B2C_PLAN §1.4).</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {ZONE_LEGEND.map((z) => (
                      <li key={z.zone} className="flex gap-3" data-testid={`zone-legend-${z.zone}`}>
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-sm font-bold font-heading ${
                            ZONE_BADGE_CLASS[z.zone]
                          }`}
                        >
                          {z.zone}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-700">{z.label}</p>
                          <p className="text-xs text-slate-500">{z.hint}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default B2CZones;
